import { useCallback, useEffect, useReducer, useRef } from "react";
import { AssistantServices, NotistackServices, WSServices } from "shared/services";
import type { SSEConfirmationMessage, SSEConfirmationRequired, SSEToolCall, StreamCallbacks } from "shared/services/AssistantServices";
import type { ChatMessage, PendingConfirmation } from "./useTeamMessages";
import { useStreamBuffer } from "./useStreamBuffer";

// ── State & Reducer ───────────────────────────────────────────────

interface AssistantState {
	conversationId: string | null;
	messages: ChatMessage[];
	isStreaming: boolean;
	loadingChat: boolean;
	activeTools: Map<string, string>;
	pendingConfirmation: PendingConfirmation | null;
}

const INITIAL_STATE: AssistantState = {
	conversationId: null,
	messages: [],
	isStreaming: false,
	loadingChat: true,
	activeTools: new Map(),
	pendingConfirmation: null,
};

type Action =
	| { type: "HISTORY_LOADED"; conversationId: string; messages: ChatMessage[] }
	| { type: "HISTORY_EMPTY" }
	| { type: "HISTORY_FAILED" }
	| { type: "CONVERSATION_CREATED"; conversationId: string }
	| { type: "MESSAGES_UPDATED"; updater: (msgs: ChatMessage[]) => ChatMessage[] }
	| { type: "STREAMING_STARTED"; userMsg: ChatMessage; assistantMsg: ChatMessage }
	| { type: "STREAMING_STOPPED" }
	| { type: "STREAMING_ERROR"; assistantMsgId: string; errorMsg: string }
	| { type: "TOOL_STARTED"; id: string; name: string }
	| { type: "TOOL_FINISHED"; id: string }
	| { type: "CONFIRMATION_SET"; confirmation: PendingConfirmation | null }
	| { type: "RESUME_STARTED"; confirmationId: string; approved: boolean; aiMsg: ChatMessage };

function reducer(state: AssistantState, action: Action): AssistantState {
	switch (action.type) {
		case "HISTORY_LOADED":
			return { ...state, conversationId: action.conversationId, messages: action.messages, loadingChat: false };

		case "HISTORY_EMPTY":
		case "HISTORY_FAILED":
			return { ...state, loadingChat: false };

		case "CONVERSATION_CREATED":
			return { ...state, conversationId: action.conversationId };

		case "MESSAGES_UPDATED":
			return { ...state, messages: action.updater(state.messages) };

		case "STREAMING_STARTED":
			return { ...state, messages: [...state.messages, action.userMsg, action.assistantMsg], isStreaming: true };

		case "STREAMING_STOPPED":
			return {
				...state,
				messages: state.messages.map((m) => (m.thinking ? { ...m, thinking: undefined, isStreaming: false } : m)),
				isStreaming: false,
				activeTools: new Map(),
			};

		case "STREAMING_ERROR":
			return {
				...state,
				messages: state.messages.map((m) =>
					m.id === action.assistantMsgId ? { ...m, thinking: undefined, error: true, errorMessage: action.errorMsg } : m,
				),
				isStreaming: false,
				activeTools: new Map(),
			};

		case "TOOL_STARTED": {
			const tools = new Map(state.activeTools);
			tools.set(action.id, action.name);
			return { ...state, activeTools: tools };
		}

		case "TOOL_FINISHED": {
			const tools = new Map(state.activeTools);
			tools.delete(action.id);
			return { ...state, activeTools: tools };
		}

		case "CONFIRMATION_SET":
			return { ...state, pendingConfirmation: action.confirmation };

		case "RESUME_STARTED":
			return {
				...state,
				messages: [
					...state.messages.map((m) => {
						if (m.senderType === "confirmation" && m.metadata?.confirmationId === action.confirmationId) {
							return { ...m, metadata: { ...m.metadata, status: action.approved ? "approved" : "denied" } };
						}
						return m;
					}),
					action.aiMsg,
				],
				isStreaming: true,
			};

		default:
			return state;
	}
}

// ── Hook ──────────────────────────────────────────────────────────

export const useAssistantMessages = (workspaceUid: string | null) => {
	const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

	const stateRef = useRef(state);
	stateRef.current = state;

	const workspaceUidRef = useRef(workspaceUid);
	workspaceUidRef.current = workspaceUid;

	const wsSubscriptionRef = useRef<{ stop: () => void } | null>(null);
	const activeAssistantMsgIdRef = useRef<string | null>(null);

	// ── Stream buffer (renderização suave RNF002) ───────────────
	const streamBuffer = useStreamBuffer({
		charsPerFrame: 3,
		onFlush: (accumulated) => {
			const msgId = activeAssistantMsgIdRef.current;
			if (!msgId) return;
			dispatch({
				type: "MESSAGES_UPDATED",
				updater: (msgs) => msgs.map((m) => (m.id === msgId ? { ...m, content: accumulated, thinking: undefined } : m)),
			});
		},
	});

	const cleanupWS = useCallback(() => {
		wsSubscriptionRef.current?.stop();
		wsSubscriptionRef.current = null;
	}, []);

	const stopStreaming = useCallback(() => {
		streamBuffer.flush();
		// Marca a mensagem como não mais em streaming e limpa thinking
		const msgId = activeAssistantMsgIdRef.current;
		if (msgId) {
			dispatch({
				type: "MESSAGES_UPDATED",
				updater: (msgs) => msgs.map((m) => (m.id === msgId ? { ...m, isStreaming: false, thinking: undefined } : m)),
			});
		}
		activeAssistantMsgIdRef.current = null;
		dispatch({ type: "STREAMING_STOPPED" });
		// Atrasar cleanup do WS para capturar eventos tardios (message_saved pode chegar após processing_completed)
		setTimeout(() => cleanupWS(), 3000);
	}, [cleanupWS, streamBuffer]);

	// Cleanup ao desmontar
	useEffect(() => () => cleanupWS(), [cleanupWS]);

	// Recarregar mensagens após reconexão do WebSocket (evita estado preso)
	useEffect(() => {
		const { stop } = WSServices.on("reconnect", async () => {
			const convId = stateRef.current.conversationId;
			if (!convId || !workspaceUidRef.current) return;
			try {
				const data = await AssistantServices.getConversation(workspaceUidRef.current, convId, 100, 0);
				dispatch({
					type: "HISTORY_LOADED",
					conversationId: convId,
					messages: data.messages
						.filter((m) => !m.metadata?.isConfirmationResponse)
						.map((m) => ({
							id: `msg-${m.id}`,
							content: m.content,
							senderType: m.senderType as ChatMessage["senderType"],
							createdAt: m.createdAt,
							metadata: m.senderType === "confirmation" ? m.metadata : undefined,
						})),
				});
				// Se estava em streaming, parar pois perdemos a conexão
				if (stateRef.current.isStreaming) {
					dispatch({ type: "STREAMING_STOPPED" });
				}
			} catch {
				// Silencioso — tentará novamente na próxima reconexão
			}
		});
		return () => stop();
	}, []);

	// Carregar histórico de conversas ao montar (quando workspaceUid está disponível)
	useEffect(() => {
		let cancelled = false;

		const loadHistory = async () => {
			try {
				const conversations = await AssistantServices.listConversations(workspaceUid);
				if (cancelled) return;

				if (conversations.length > 0) {
					const latest = [...conversations].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
					const data = await AssistantServices.getConversation(workspaceUid, latest.id, 100, 0);
					if (cancelled) return;

					dispatch({
						type: "HISTORY_LOADED",
						conversationId: latest.id,
						messages: data.messages
							.filter((m) => !m.metadata?.isConfirmationResponse)
							.map((m) => ({
								id: `msg-${m.id}`,
								content: m.content,
								senderType: m.senderType as ChatMessage["senderType"],
								createdAt: m.createdAt,
								metadata: m.senderType === "confirmation" ? m.metadata : undefined,
							})),
					});
				} else {
					dispatch({ type: "HISTORY_EMPTY" });
				}
			} catch {
				if (!cancelled) dispatch({ type: "HISTORY_FAILED" });
			}
		};

		loadHistory();
		return () => {
			cancelled = true;
		};
	}, [workspaceUid]);

	// ── Inscrição WebSocket ─────────────────────────────────────

	const subscribeToWS = useCallback(
		(convId: string, assistantMsgId: string) => {
			cleanupWS();
			activeAssistantMsgIdRef.current = assistantMsgId;

			const updateAssistantMsg = (updater: (msg: ChatMessage) => ChatMessage) => {
				dispatch({ type: "MESSAGES_UPDATED", updater: (msgs) => msgs.map((m) => (m.id === assistantMsgId ? updater(m) : m)) });
			};

			const messageSavedRef = { received: false };

			const callbacks: StreamCallbacks = {
				onProcessingStarted: () => {
					updateAssistantMsg((m) => ({ ...m, thinking: "Processando..." }));
				},

				onToken: (data) => {
					const token = data.token || data.content || "";
					if (token) streamBuffer.push(token);
				},

				onToolCall: (data: SSEToolCall) => {
					dispatch({ type: "TOOL_STARTED", id: data.id, name: data.name });
					updateAssistantMsg((m) => ({ ...m, thinking: m.content ? undefined : `Executando: ${data.name}...` }));
				},

				onToolResult: (data) => {
					dispatch({ type: "TOOL_FINISHED", id: data.id });
				},

				onConfirmationRequired: (data: SSEConfirmationRequired) => {
					// Legacy: estado intermediário durante tool loop
					updateAssistantMsg((m) => ({ ...m, thinking: m.content ? undefined : "Aguardando sua confirmação..." }));
				},

				onConfirmationMessage: (data: SSEConfirmationMessage) => {
					try {
						const msg = data?.message;
						if (!msg || !msg.id) {
							console.warn("[useAssistantMessages] confirmation_message recebido com dados inválidos", data);
							return;
						}
						dispatch({
							type: "MESSAGES_UPDATED",
							updater: (msgs) => {
								const filtered = msgs.filter((m) => !(m.id === assistantMsgId && !m.content));
								return [
									...filtered,
									{
										id: `msg-${msg.id}`,
										content: msg.content,
										senderType: "confirmation" as const,
										senderRef: msg.senderRef,
										createdAt: msg.createdAt,
										metadata: msg.metadata,
									},
								];
							},
						});
					} catch (err) {
						console.error("[useAssistantMessages] Erro ao processar confirmation_message:", err, data);
					}
				},

				onConfirmationResolved: () => {
					updateAssistantMsg((m) => ({ ...m, thinking: m.content ? undefined : "Continuando..." }));
				},

				onComplete: (data) => {
					streamBuffer.flush();
					updateAssistantMsg((m) => ({ ...m, content: data.content || m.content, thinking: undefined }));
				},

				onMessageSaved: (data) => {
					messageSavedRef.received = true;
					const savedAiMsg = data.assistantMessage ?? data.leaderMessage;
					dispatch({
						type: "MESSAGES_UPDATED",
						updater: (msgs) =>
							msgs.map((m) => {
								if (data.userMessage && m.senderType === "user" && m.id.startsWith("user-")) {
									return {
										...m,
										id: `msg-${data.userMessage.id}`,
										content: data.userMessage.content,
										createdAt: data.userMessage.createdAt,
									};
								}
								if (savedAiMsg && m.id === assistantMsgId) {
									activeAssistantMsgIdRef.current = `msg-${savedAiMsg.id}`;
									return {
										...m,
										id: `msg-${savedAiMsg.id}`,
										content: savedAiMsg.content,
										thinking: undefined,
										createdAt: savedAiMsg.createdAt,
										isStreaming: m.isStreaming,
									};
								}
								return m;
							}),
					});
				},

				onProcessingStatus: (data) => {
					if (data.currentStep) {
						updateAssistantMsg((m) => (!m.content ? { ...m, thinking: data.currentStep } : m));
					}
				},

				onProcessingCompleted: () => {
					// Se message_saved já chegou, parar imediatamente
					// Caso contrário, aguardar um pouco para message_saved chegar
					if (messageSavedRef.received) {
						stopStreaming();
					} else {
						setTimeout(() => stopStreaming(), 1500);
					}
				},

				onProcessingError: (data) => {
					const errorMsg = data.error || "Erro no processamento.";
					dispatch({ type: "STREAMING_ERROR", assistantMsgId, errorMsg });
					cleanupWS();
					NotistackServices.showErrorMessage(errorMsg);
				},

				onDone: () => stopStreaming(),

				onError: (data) => {
					const errorMsg = data?.message || "Ocorreu um erro inesperado.";
					dispatch({ type: "STREAMING_ERROR", assistantMsgId, errorMsg });
					cleanupWS();
				},
			};

			wsSubscriptionRef.current = AssistantServices.subscribeToConversation(convId, callbacks);
		},
		[cleanupWS, stopStreaming, streamBuffer],
	);

	// ── Garantir conversa ───────────────────────────────────────

	const ensureConversation = useCallback(async (): Promise<string> => {
		const currentConvId = stateRef.current.conversationId;
		if (currentConvId) return currentConvId;

		const conversation = await AssistantServices.createConversation(workspaceUidRef.current, "Configuração Inicial");
		dispatch({ type: "CONVERSATION_CREATED", conversationId: conversation.id });
		return conversation.id;
	}, []);

	// ── Enviar mensagem ─────────────────────────────────────────

	const sendMessage = useCallback(
		async (content: string) => {
			const trimmed = content.trim();
			if (stateRef.current.isStreaming || !trimmed) return;

			const now = new Date().toISOString();
			const assistantMsgId = `assistant-${Date.now()}`;
			const userMsg: ChatMessage = { id: `user-${Date.now()}`, content: trimmed, senderType: "user", createdAt: now };
			const assistantMsg: ChatMessage = {
				id: assistantMsgId,
				content: "",
				senderType: "assistente",
				thinking: "Pensando...",
				createdAt: now,
				isStreaming: true,
			};

			dispatch({ type: "STREAMING_STARTED", userMsg, assistantMsg });
			streamBuffer.reset();

			try {
				const convId = await ensureConversation();
				subscribeToWS(convId, assistantMsgId);
				await AssistantServices.sendMessage(workspaceUidRef.current, convId, trimmed);
			} catch (error: any) {
				const errorMsg = error?.message || "Não foi possível enviar a mensagem. Tente novamente.";
				dispatch({ type: "STREAMING_ERROR", assistantMsgId, errorMsg });
				cleanupWS();
			}
		},
		[ensureConversation, subscribeToWS, cleanupWS, streamBuffer],
	);

	// ── Confirmar/negar ferramenta (inline no chat) ────────────

	const handleConfirmation = useCallback(
		async (confirmationId: string, approved: boolean, reason?: string) => {
			const convId = stateRef.current.conversationId;
			if (!convId || stateRef.current.isStreaming) return;

			const assistantMsgId = `assistant-${Date.now()}`;

			dispatch({
				type: "RESUME_STARTED",
				confirmationId,
				approved,
				aiMsg: {
					id: assistantMsgId,
					content: "",
					senderType: "assistente",
					thinking: approved ? "Confirmação aprovada, executando..." : "Negado, retomando...",
					createdAt: new Date().toISOString(),
					isStreaming: true,
				},
			});

			streamBuffer.reset();
			subscribeToWS(convId, assistantMsgId);

			try {
				await AssistantServices.confirmToolExecution(confirmationId, approved, reason);
			} catch (error: any) {
				const errorMsg = error?.message || "Erro ao processar confirmação.";
				dispatch({ type: "STREAMING_ERROR", assistantMsgId, errorMsg });
				cleanupWS();
				NotistackServices.showErrorMessage(errorMsg);
			}
		},
		[subscribeToWS, cleanupWS, streamBuffer],
	);

	// ── API pública ─────────────────────────────────────────────

	return {
		messages: state.messages,
		isStreaming: state.isStreaming,
		loadingChat: state.loadingChat,
		activeTools: state.activeTools,
		sendMessage,
		handleConfirmation,
	};
};
